import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Button } from '../components/ui/Button';
import { ErrorBanner } from '../components/ui/ErrorBanner';
import { PageHeader } from '../components/ui/PageHeader';
import { Select } from '../components/ui/Select';
import { api } from '../lib/api';
import { kontrollgaengeApi } from '../lib/kontrollgaenge-api';
import { requireAccess } from '../lib/guards';

export const Route = createFileRoute('/kontrollgaenge/neu')({
  beforeLoad: () => requireAccess('/api/v1/kontrollgaenge'),
  component: NeuerKontrollgangPage,
});

interface ObjektListItem {
  id: string;
  bezeichnung: string;
}

export function NeuerKontrollgangPage(): JSX.Element {
  const navigate = useNavigate();
  const [objektId, setObjektId] = useState('');
  const [checklisteId, setChecklisteId] = useState('');

  const objekteQ = useQuery({
    queryKey: ['objekte', 'list'],
    queryFn: () => api<{ items: ObjektListItem[] }>('/api/v1/objekte'),
  });

  const checklistenQ = useQuery({
    queryKey: ['kontrollgaenge', 'checklisten'],
    queryFn: () => kontrollgaengeApi.listChecklisten(),
  });

  const start = useMutation({
    mutationFn: () => kontrollgaengeApi.start({ objektId, checklisteId }),
    onSuccess: ({ item }) => {
      void navigate({ to: '/kontrollgaenge/$id', params: { id: item.id } });
    },
  });

  const checklisten = (checklistenQ.data?.items ?? []).filter((c) => c.aktiv);
  const canSubmit = objektId.length > 0 && checklisteId.length > 0 && !start.isPending;

  return (
    <div className="max-w-2xl space-y-6">
      <PageHeader
        title="Neuer Kontrollgang"
        description="Objekt + Checkliste wählen — Items werden danach am Ort abgehakt."
      />

      {start.error ? <ErrorBanner error={start.error as Error} /> : null}

      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (canSubmit) start.mutate();
        }}
      >
        <label className="block">
          <span className="mb-1 block text-sm font-medium text-slate-700">Objekt</span>
          <Select value={objektId} onChange={(e) => setObjektId(e.target.value)} required>
            <option value="" disabled>
              – Objekt wählen –
            </option>
            {(objekteQ.data?.items ?? []).map((o) => (
              <option key={o.id} value={o.id}>
                {o.bezeichnung}
              </option>
            ))}
          </Select>
        </label>

        <label className="block">
          <span className="mb-1 block text-sm font-medium text-slate-700">Checkliste</span>
          <Select
            value={checklisteId}
            onChange={(e) => setChecklisteId(e.target.value)}
            required
            disabled={checklisten.length === 0}
          >
            <option value="" disabled>
              {checklisten.length === 0 ? '— Keine aktive Checkliste —' : '– Checkliste wählen –'}
            </option>
            {checklisten.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name} ({c.itemsJsonb.length} Items)
              </option>
            ))}
          </Select>
        </label>

        <div className="flex gap-3">
          <Button type="submit" disabled={!canSubmit}>
            {start.isPending ? 'Starte…' : 'Kontrollgang starten'}
          </Button>
          <Button
            type="button"
            variant="ghost"
            onClick={() => void navigate({ to: '/kontrollgaenge' })}
          >
            Abbrechen
          </Button>
        </div>
      </form>
    </div>
  );
}
